import { buildNodesList, findTextRanges, findDomRanges, prepareString, tokenizeString } from './lib';

export type ServerHighlight = {
  field_name: string;
  highlight_part: string;
};
export type ServerHighlights = Record<string, ServerHighlight[]>;

export type UseServerHighlightsParams = {
  applicantId: number;
  fieldName?: string;
  wholeField?: boolean;
};

let terms: string[] = [];
let serverHighlights: ServerHighlights = {};
const ranges = new Map<HTMLElement, Range[]>();

let highlight: Highlight | null = null;
function initHighlight() {
  try {
    highlight = new Highlight();
    CSS.highlights.set('search', highlight);
    return true;
  } catch {
    return false;
  }
}

const highlightIsSupported = initHighlight();

function prepareTerms(terms: string[]) {
  return terms.map((t) => t.trim())
    // так можно всю страницу подсветить, удачи в поиске сишника
    .filter((t) => t.length > 1);
}

export function updateHighlightsQuery(q: string, newServerHighlights: ServerHighlights = {}) {
  /**
   * Здесь не обновляется ничего кроме термов потому что
   * при поиске у нас все перезагружается и все рэнджи
   * пересчитаются сами
   */
  terms = prepareTerms(tokenizeString(prepareString(q)));
  serverHighlights = newServerHighlights;
}

export function resetHighlightsQuery() {
  terms = [];
  ranges.clear();
  highlight?.clear();
}

function clearElementDomRnages(el: HTMLElement) {
  if (ranges.has(el)) {
    ranges.get(el)?.forEach((range) => {
      highlight?.delete(range);
    });
    ranges.delete(el);
  }
}

function getServerHighlights(applicantId: number, fieldName?: string) {
  const applicantHighlights = serverHighlights[String(applicantId)];
  if (!applicantHighlights) {
    return [];
  }

  return (
    fieldName
      ? applicantHighlights.filter(({ field_name }) => fieldName === field_name)
      : applicantHighlights
  ).map(({ highlight_part }) => highlight_part).filter(Boolean);
}

function shouldHighlightWholeField(params: UseServerHighlightsParams) {
  const { applicantId, fieldName, wholeField } = params;
  if (!wholeField || !fieldName) {
    return false;
  }
  return (serverHighlights[String(applicantId)] || []).some((h) => h.field_name === fieldName);
}

function getWholeElementRange(el: HTMLElement) {
  const fullRange = new Range();
  fullRange.selectNodeContents(el);
  return fullRange;
}

function highlightElement(el: HTMLElement, useServerHighlights?: UseServerHighlightsParams) {
  // nothing to do
  if (terms.length === 0 && !useServerHighlights) {
    return;
  }

  clearElementDomRnages(el);

  if (useServerHighlights && shouldHighlightWholeField(useServerHighlights)) {
    const fullRange = getWholeElementRange(el);
    highlight?.add(fullRange);
    ranges.set(el, [fullRange]);
    return;
  }

  const { nodesList, text: fullText } = buildNodesList(el);
  const textRanges = findTextRanges(
    prepareString(fullText),
    useServerHighlights ? getServerHighlights(useServerHighlights.applicantId, useServerHighlights.fieldName) : terms
  );
  const domRanges = findDomRanges(textRanges, nodesList);

  domRanges.forEach((range) => {
    highlight?.add(range);
  });

  ranges.set(el, domRanges);
}

export function addHighlightableElement(el: HTMLElement, useServerHighlights?: UseServerHighlightsParams) {
  if (highlightIsSupported) {
    highlightElement(el, useServerHighlights);
  }
}

export function updateHighlightableElement(el: HTMLElement, useServerHighlights?: UseServerHighlightsParams) {
  if (highlightIsSupported) {
    highlightElement(el, useServerHighlights);
  }
}

export function removeHighlightableElement(el: HTMLElement) {
  if (highlightIsSupported) {
    clearElementDomRnages(el);
  }
}
